import { useCallback, useEffect, useState } from 'react';

import { CaseDetail } from './components/CaseDetail.tsx';
import { CaseList } from './components/CaseList.tsx';
import { DsarRequestForm } from './components/DsarRequestForm.tsx';
import { NotificationsFeed } from './components/NotificationsFeed.tsx';
import { ProfileBadge } from './components/ProfileBadge.tsx';
import { ThemeToggle } from './components/ThemeToggle.tsx';
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  PageHeader,
  SkeletonText,
  TabPanel,
  Tabs,
  type TabItem,
} from './components/ui.tsx';
import {
  portalApi,
  type PortalCase,
  type PortalDocument,
  type PortalNotification,
  type PortalProfile,
  type PortalReport,
} from './lib/api.ts';
import { describeError, type ErrorInfo } from './lib/request-id.ts';

interface Detail {
  caseRecord: PortalCase;
  documents: PortalDocument[];
  reports: PortalReport[];
}

type View = 'cases' | 'dsar';

/** The portal's two sections, rendered as the shared Tabs under the page header. */
const SECTIONS: readonly TabItem<View>[] = [
  { value: 'cases', label: 'Cases' },
  { value: 'dsar', label: 'Data rights' },
];
const SECTIONS_ID = 'portal-sections';

/** A column of placeholder cards while the detail (or the whole page) loads. */
function LoadingCards({ label }: { label: string }): JSX.Element {
  return (
    <div className="stack">
      <Card>
        <CardBody>
          <SkeletonText lines={3} label={label} />
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <SkeletonText lines={4} label="" />
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * The read-only Client Portal shell (T-7.10; US-100). Loads the client's own profile,
 * case list and notifications on mount; opening a case loads that case's documents +
 * shared reports. Every view is READ-ONLY apart from two client actions — the T-7.11
 * upload and the T-7.14 DSAR request. All data is already scoped server-side to the
 * caller's own client memberships (CC-1/CC-4).
 *
 * The chrome is the firm app's (PO ruling 2026-10-05: identical look, reaffirmed
 * 2026-10-08 for the wordmark lockup): the same `.argus-topnav` as apps/web (the ARGUS
 * icon + wordmark lockup, the spacer, then the actions — theme toggle + the client's
 * profile badge), and one shared `PageHeader` with the two sections switched by the shared
 * `Tabs` underneath it.
 */
export function App(): JSX.Element {
  const [profile, setProfile] = useState<PortalProfile | null>(null);
  const [cases, setCases] = useState<PortalCase[]>([]);
  const [notifications, setNotifications] = useState<PortalNotification[]>([]);
  const [loadError, setLoadError] = useState<ErrorInfo | null>(null);
  const [loading, setLoading] = useState(true);
  // Bumped by "Try again" on the initial-load error; re-runs the mount fetch.
  const [loadAttempt, setLoadAttempt] = useState(0);

  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [detailError, setDetailError] = useState<ErrorInfo | null>(null);

  // The section tabs switch the cases surface and the data-rights surface.
  const [view, setView] = useState<View>('cases');

  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const [p, cs, ns] = await Promise.all([
          portalApi.getProfile(),
          portalApi.listCases(),
          portalApi.listNotifications(),
        ]);
        if (!live) return;
        setProfile(p);
        setCases(cs);
        setNotifications(ns);
      } catch (err) {
        if (live) setLoadError(describeError(err));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [loadAttempt]);

  const openCase = useCallback(
    (caseNumber: string) => {
      if (!cases.some((c) => c.case_number === caseNumber)) return;
      setSelected(caseNumber);
      setDetail(null);
      setDetailError(null);
      (async () => {
        try {
          // Fetch the SINGLE-case view (not the list row) so the detail carries
          // `uploads_enabled` — the per-firm flag that gates the T-7.11 upload control.
          const [caseRecord, documents, reports] = await Promise.all([
            portalApi.getCase(caseNumber),
            portalApi.listDocuments(caseNumber),
            portalApi.listReports(caseNumber),
          ]);
          setDetail({ caseRecord, documents, reports });
        } catch (err) {
          setDetailError(describeError(err));
        }
      })();
    },
    [cases],
  );

  const ready = !loading && !loadError;

  return (
    <div className="argus-app">
      {/* The same top bar as apps/web (components/layouts/top-nav.tsx): the ARGUS icon +
          wordmark lockup (PO 2026-10-08 — same assets, same `.argus-brand-*` classes and
          light/dark swap mechanism as apps/web; see that file's header comment for the
          cap-height arithmetic), the spacer, then the actions. The portal has no sidebar —
          no menu / collapse toggle, so no back/forward history arrows either (those sit
          beside apps/web's collapse toggle, which the portal doesn't have); the wordmark
          never needs a "sidebar collapsed" icon-only fallback for the same reason. */}
      <header className="argus-topnav">
        <a href="/" className="argus-topnav__brand" aria-label="ARGUS client portal">
          <img
            className="argus-brand-icon argus-brand-icon--light"
            src="/icons/argus-mark-light.png"
            alt=""
            width={28}
            height={28}
          />
          <img
            className="argus-brand-icon argus-brand-icon--dark"
            src="/icons/argus-mark-dark.png"
            alt=""
            width={28}
            height={28}
          />
          <span className="argus-brand-wordmark-wrap argus-topnav__hide-mobile">
            <img
              className="argus-brand-wordmark argus-brand-wordmark--light"
              src="/brand/argus-wordmark-light.png"
              alt="ARGUS"
              width={623}
              height={220}
            />
            <img
              className="argus-brand-wordmark argus-brand-wordmark--dark"
              src="/brand/argus-wordmark-dark.png"
              alt="ARGUS"
              width={623}
              height={218}
            />
          </span>
        </a>
        <span className="argus-topnav__spacer" />
        <div className="argus-topnav__actions">
          <ThemeToggle />
          {profile ? <ProfileBadge profile={profile} /> : null}
        </div>
      </header>

      <main className="argus-app__main">
        <div className="argus-page portal-page">
          <PageHeader
            title="Client portal"
            description="Your cases, shared documents and reports, plus the requests your firm has made of you."
          />
          <Tabs
            id={SECTIONS_ID}
            aria-label="Portal sections"
            items={SECTIONS}
            value={view}
            onChange={setView}
          />
          <TabPanel baseId={SECTIONS_ID} value={view}>
            {loading ? (
              <div className="portal-grid">
                <div className="portal-grid__column">
                  <LoadingCards label="Loading your cases" />
                </div>
                <div className="portal-grid__column">
                  <LoadingCards label="" />
                </div>
              </div>
            ) : null}

            {loadError ? (
              <ErrorState
                title="Your portal could not be loaded"
                message={loadError.message}
                requestId={loadError.requestId}
                onRetry={() => setLoadAttempt((n) => n + 1)}
              />
            ) : null}

            {ready && view === 'dsar' ? <DsarRequestForm cases={cases} /> : null}

            {ready && view === 'cases' ? (
              <div className="portal-grid">
                <div className="portal-grid__column">
                  <Card aria-label="Cases">
                    <CardHeader
                      title="Cases"
                      description={cases.length === 1 ? '1 case' : `${cases.length} cases`}
                    />
                    <CardBody flush>
                      <CaseList cases={cases} selected={selected} onSelect={openCase} />
                    </CardBody>
                  </Card>

                  <Card as="article" aria-label="Notifications">
                    <CardHeader
                      title="Notifications"
                      description="Report releases and case milestones"
                    />
                    <CardBody>
                      <NotificationsFeed notifications={notifications} />
                    </CardBody>
                  </Card>
                </div>

                <div className="portal-grid__column">
                  {detailError ? (
                    <ErrorState
                      title="This case could not be loaded"
                      message={detailError.message}
                      requestId={detailError.requestId}
                      onRetry={() => (selected ? openCase(selected) : undefined)}
                    />
                  ) : detail ? (
                    <CaseDetail
                      caseRecord={detail.caseRecord}
                      documents={detail.documents}
                      reports={detail.reports}
                      onUploaded={() => openCase(detail.caseRecord.case_number)}
                    />
                  ) : selected ? (
                    <LoadingCards label="Loading case" />
                  ) : (
                    <Card>
                      <CardBody>
                        <EmptyState
                          title="No case selected"
                          description="Select a case to view its documents and reports."
                        />
                      </CardBody>
                    </Card>
                  )}
                </div>
              </div>
            ) : null}
          </TabPanel>
        </div>
      </main>
    </div>
  );
}
